import React, { useState, useEffect, useRef } from "react";
import "../styles/components/all-games-row.css";
import GameCard from "./game-card";
import { MdNavigateBefore, MdNavigateNext } from "react-icons/md";
import { gameType } from "../@types/game-type";

interface allGamesRowProps {
  games: gameType[] | undefined;
  handleClick: (game: gameType) => void;
}

export default function AllGamesRow(props: allGamesRowProps) {
  const [scrollX, setScrollX] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const handleResize = () => {
    setScrollX(0); // Reset position on resize
  };

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleMove = (direction: "left" | "right") => {
    if (!containerRef.current || !listRef.current) return;

    const containerWidth = containerRef.current.offsetWidth;
    const cardWidth = parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue("--card-width")
    );
    const gap = parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue("--row-gap")
    );

    // Calculate total width including gaps
    const cardWithGap = cardWidth + gap;
    const visibleCards = Math.floor(containerWidth / cardWithGap);
    const moveAmount = visibleCards * cardWithGap;

    let newScrollX = direction === "left"
      ? scrollX + moveAmount
      : scrollX - moveAmount;

    // Boundary checks
    if (newScrollX > 0) newScrollX = 0;

    const totalWidth = Math.ceil((props.games?.length || 0) / 2) * cardWithGap;
    const minScroll = -(totalWidth - containerWidth + gap);

    if (newScrollX < minScroll) newScrollX = minScroll;
    setScrollX(newScrollX);
  };

  // Update visibility logic for navigation arrows
  const canScrollLeft = scrollX < 0;
  const canScrollRight = containerRef.current && listRef.current &&
    scrollX > -(Math.max(listRef.current.scrollWidth - containerRef.current.offsetWidth, 0));

  // Calculate total width including gaps for both rows
  const totalCards = props.games?.length || 0;
  const topRowGames = props.games?.filter((_, index) => index % 2 === 0) || [];
  const bottomRowGames = props.games?.filter((_, index) => index % 2 === 1) || [];

  const scrollLeft = () => {
    if (containerRef.current) {
      containerRef.current.scrollBy({
        left: -300,
        behavior: "smooth"
      });
    }
  };

  const scrollRight = () => {
    if (containerRef.current) {
      containerRef.current.scrollBy({
        left: 300,
        behavior: "smooth"
      });
    }
  };

  return (
    <div className="list-area-all-games-row">
      <div className="navigate-before-all-games-row" onClick={scrollLeft}>
        <MdNavigateBefore />
      </div>
      <div className="container-list-all-games-row" ref={containerRef}>
        <div
          className="list-all-games-row"
          ref={listRef}
          style={{
            transform: `translateX(${scrollX}px)`,
            width: "max-content" // Add this to ensure proper width calculation
          }}
        >
          <div className="games-row top-row">
            {topRowGames.map((game) => (
              <GameCard
                key={game.id}
                game={game}
                isAllGamesPage={false}
                handleClick={props.handleClick}
              />
            ))}
          </div>
          <div className="games-row bottom-row">
            {bottomRowGames.map((game) => (
              <GameCard
                key={game.id}
                game={game}
                isAllGamesPage={false}
                handleClick={props.handleClick}
              />
            ))}
          </div>
        </div>
      </div>
      <div className="navigate-next-all-games-row" onClick={scrollRight}>
        <MdNavigateNext />
      </div>
    </div>
  );
}
